import React, { useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { fetchLenderConfig, resetDealFilters } from '../../../store/investor/action';

import DealsList from './components/DealsList/DealsList';
import ManageDealDrawer from '../../../common/ManageDealDrawer/ManageDealDrawer';

import styles from './Deals.module.scss';
import DealTermsLoggingDrawer from './components/DealsList/DealTermsLoggingDrawer/DealTermsLoggingDrawer';
import { useSearchParams } from 'react-router-dom';
import { closeCommentsThread } from '../../../store/commentsThread/commentsThread_actions';
import useLegacyBusinessDataFetch from '../../DataRoom/useLegacyBusinessDataFetch';
import { updateQueryParams } from '../../../utils/searchParamUtils';
import AskAicaV2 from '../../Dashboard/DashboardModules/Overview/components/AskAicaV2/AskAicaV2';

interface DealsProps {
    screen: string;
}

export const DEALS_SCREENS = {
    GLOBAL_DEALS: 'GLOBAL_DEALS',
    COMPANY_DEALS: 'COMPANY_DEALS',
};

const Deals: React.FC<DealsProps> = ({ screen }) => {
    const dispatch = useAppDispatch();
    const [queryParams, setQueryParams] = useSearchParams();

    const userData = useAppSelector((state) => state.user.userData);
    const user = useAppSelector((state) => state.user);
    const dealDrawerState = useAppSelector((state) => state.investor.dealDrawer);
    const showDealDrawer = dealDrawerState.open;

    const { selectedCompany } = useLegacyBusinessDataFetch();

    // Combined state for deal terms logging
    const [dealTermsLogging, setDealTermsLogging] = useState({
        isDrawerOpen: false,
        investorDealId: '',
        borrowerName: '',
        investorName: '',
        source: '',
        restrictBackwardMovement: false,
    });

    useEffect(() => {
        const paramMapping = {
            showDealTermsLoggingDrawer: 'isDrawerOpen',
            investorDealId: 'investorDealId',
            borrowerName: 'borrowerName',
            investorName: 'investorName',
            source: 'source',
            restrictBackwardMovement: 'restrictBackwardMovement',
        };

        if (queryParams.get('showDealTermsLoggingDrawer')) {
            const newState: typeof dealTermsLogging = { ...dealTermsLogging };

            Object.entries(paramMapping).forEach(([paramKey, stateKey]) => {
                if (paramKey === 'showDealTermsLoggingDrawer') {
                    newState[stateKey] = true;
                } else if (paramKey === 'restrictBackwardMovement') {
                    newState[stateKey] = queryParams.get(paramKey) === 'true';
                } else {
                    newState[stateKey] = queryParams.get(paramKey) || '';
                }
            });

            setDealTermsLogging(newState);
        }
    }, [queryParams]);

    useEffect(() => {
        dispatch(
            fetchLenderConfig({
                accountType: userData.accountType,
                primaryEmail: userData.primaryEmail,
                investorOrgId: userData.investorOrgId,
                userRole: user?.userData?.roles?.[0],
                configName: 'AICA_LENDER_DASHBOARD',
            }),
        );

        return () => {
            dispatch(closeCommentsThread());
        };
    }, []);

    useEffect(() => {
        return () => {
            if (screen === DEALS_SCREENS.COMPANY_DEALS) {
                dispatch(
                    resetDealFilters({
                        allStatusesSelected: true,
                    }),
                );
            }
        };
    }, [screen]);
    return (
        <div className={styles.DealsContainer}>
            <DealsList selectedCompany={selectedCompany} />
            {showDealDrawer && dealDrawerState.drawerSource !== 'ProfileButton' && (
                <ManageDealDrawer source={dealDrawerState.drawerSource || 'Deals List Page'} />
            )}
            {dealTermsLogging.isDrawerOpen && (
                <DealTermsLoggingDrawer
                    isOpen={dealTermsLogging.isDrawerOpen}
                    onClose={() => {
                        setDealTermsLogging((prev) => ({ ...prev, isDrawerOpen: false }));
                        [
                            'showDealTermsLoggingDrawer',
                            'investorDealId',
                            'borrowerName',
                            'source',
                            'investorName',
                            'restrictBackwardMovement',
                        ].forEach((param) => {
                            queryParams.delete(param);
                        });
                        updateQueryParams(queryParams, setQueryParams, { dealUpdated: true }, location);
                    }}
                    investorDealId={dealTermsLogging.investorDealId}
                    borrowerName={dealTermsLogging.borrowerName}
                    investorName={dealTermsLogging.investorName}
                    source={dealTermsLogging.source}
                    restrictBackwardMovement={dealTermsLogging.restrictBackwardMovement}
                />
            )}

            {screen === DEALS_SCREENS.COMPANY_DEALS && <AskAicaV2 selectedCompany={selectedCompany} global />}
        </div>
    );
};

export default Deals;
